import { useEffect, type RefObject } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/** Delay between the page subtree mounting and the effect querying it (production value). */
export const REVEAL_DELAY_MS = 100;
/** Below this width at effect time distances shrink to 40 % and pointer tilt is off. */
export const REVEAL_MOBILE_BELOW_PX = 768;

/** Distance factor: 0.4 below 768 px, otherwise 1. */
export function revealFactor(viewportWidth: number): number {
  return viewportWidth < REVEAL_MOBILE_BELOW_PX ? 0.4 : 1;
}

function all(scope: Element, selector: string): HTMLElement[] {
  return Array.from(scope.querySelectorAll<HTMLElement>(selector));
}

const once = (trigger: Element, start: string) =>
  ({ trigger, start, toggleActions: 'play none none none', once: true }) as const;

/**
 * GSAP + ScrollTrigger reveal and scrub effects of the app shell (bundle `qs()` effect,
 * docs/behavior/hero-and-motion.md §8.2 table rows 1–11, verbatim).
 *
 * HM-1: arming is deterministic — the caller mounts this after the page subtree has committed.
 * HM-4: everything is created inside a `gsap.context` scoped to `scope` and reverted when the
 * page changes, so no trigger can strand and no other component's triggers are killed.
 */
export function armScrollReveal(scope: HTMLElement, viewportWidth: number): () => void {
  const mobile = viewportWidth < REVEAL_MOBILE_BELOW_PX;
  const t = revealFactor(viewportWidth);
  const detach: (() => void)[] = [];

  const tilt = (element: HTMLElement, move: (nx: number, ny: number) => void, leave: () => void) => {
    const onMove = (event: MouseEvent) => {
      const rect = element.getBoundingClientRect();
      move((event.clientX - rect.left) / rect.width - 0.5, (event.clientY - rect.top) / rect.height - 0.5);
    };
    element.addEventListener('mousemove', onMove);
    element.addEventListener('mouseleave', leave);
    detach.push(() => {
      element.removeEventListener('mousemove', onMove);
      element.removeEventListener('mouseleave', leave);
    });
  };

  const context = gsap.context(() => {
    // 1 · section contents
    all(scope, '.parallax-section').forEach((section) => {
      const targets = section.querySelectorAll(
        '.section__header, .section__title, .section__subtitle, .product-grid, .product-card, .glass-card, .video-player, .btn',
      );
      if (targets.length === 0) return;
      gsap.set(targets, { opacity: 0, y: 14 * t, scale: 0.985, willChange: 'transform, opacity' });
      gsap.to(targets, {
        opacity: 1,
        y: 0,
        scale: 1,
        stagger: 0,
        duration: 0.3,
        ease: 'power3.out',
        clearProps: 'transform,filter,willChange',
        scrollTrigger: once(section, 'top 96%'),
      });
    });
    // 2 · section titles
    all(scope, '.section__title').forEach((title) => {
      gsap.set(title, { clipPath: 'inset(0 100% 0 0)', opacity: 0 });
      gsap.to(title, {
        clipPath: 'inset(0 0% 0 0)',
        opacity: 1,
        duration: 1.2,
        ease: 'power4.out',
        scrollTrigger: once(title, 'top 85%'),
      });
    });
    // 3 · section subtitles
    all(scope, '.section__subtitle').forEach((subtitle) => {
      gsap.set(subtitle, { opacity: 0, filter: 'blur(8px)', y: 12 * t });
      gsap.to(subtitle, {
        opacity: 1,
        filter: 'blur(0px)',
        y: 0,
        duration: 0.3,
        delay: 0,
        ease: 'power2.out',
        scrollTrigger: once(subtitle, 'top 96%'),
      });
    });
    // 4 · product grids (+ 4b pointer tilt on desktop)
    all(scope, '.product-grid').forEach((grid) => {
      const cards = all(grid, '.product-card');
      if (cards.length === 0) return;
      gsap.set(cards, { opacity: 0, y: 14 * t, rotateX: 0, scale: 0.985, willChange: 'transform, opacity' });
      gsap.to(cards, {
        opacity: 1,
        y: 0,
        rotateX: 0,
        scale: 1,
        stagger: 0,
        duration: 0.3,
        ease: 'power3.out',
        clearProps: 'transform,filter,willChange',
        scrollTrigger: once(grid, 'top 96%'),
      });
      if (mobile) return;
      cards.forEach((card) => {
        tilt(
          card,
          (nx, ny) => {
            gsap.to(card, { rotateY: 8 * nx, rotateX: 6 * -ny, scale: 1.03, duration: 0.4, ease: 'power2.out' });
          },
          () => {
            gsap.to(card, { rotateY: 0, rotateX: 0, scale: 1, duration: 0.6, ease: 'power2.out' });
          },
        );
      });
    });
    // 5 · depth scrub
    all(scope, '[data-depth]').forEach((element) => {
      const depth = Number.parseFloat(element.dataset.depth ?? '') || 0.2;
      gsap.to(element, {
        y: 200 * depth * t,
        ease: 'none',
        scrollTrigger: { trigger: element.parentElement ?? element, start: 'top bottom', end: 'bottom top', scrub: 1 },
      });
    });
    // 6 · background scrub
    all(scope, '.parallax-bg').forEach((element) => {
      gsap.to(element, {
        yPercent: 25 * t,
        ease: 'none',
        scrollTrigger: { trigger: element.parentElement, start: 'top bottom', end: 'bottom top', scrub: 1 },
      });
    });
    // 7 · data-parallax scrub
    all(scope, '[data-parallax]').forEach((element) => {
      const value = Number.parseFloat(element.dataset.parallax ?? '') || 0.3;
      gsap.to(element, {
        yPercent: 100 * value * t,
        ease: 'none',
        scrollTrigger: { trigger: element, start: 'top bottom', end: 'bottom top', scrub: 1.5 },
      });
    });
    // 8 · glass cards (+ 8b pointer tilt on desktop)
    all(scope, '.glass-card').forEach((card) => {
      gsap.set(card, { opacity: 0, y: 14 * t, scale: 0.985, willChange: 'transform, opacity' });
      gsap.to(card, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.3,
        ease: 'power3.out',
        clearProps: 'transform,filter,willChange',
        scrollTrigger: once(card, 'top 96%'),
      });
      if (mobile) return;
      tilt(
        card,
        (nx, ny) => {
          gsap.to(card, { rotateY: 6 * nx, rotateX: 4 * -ny, duration: 0.5, ease: 'power2.out' });
        },
        () => {
          gsap.to(card, { rotateY: 0, rotateX: 0, duration: 0.8, ease: 'elastic.out(1, 0.5)' });
        },
      );
    });
    // 9 · Ω marks
    all(scope, '.hero__ohm').forEach((element) => {
      gsap.set(element, { scale: 0, opacity: 0 });
      gsap.to(element, {
        scale: 1,
        opacity: 1,
        duration: 1.2,
        ease: 'elastic.out(1, 0.4)',
        scrollTrigger: once(element, 'top 85%'),
      });
    });
    // 10 · section buttons
    all(scope, '.section .btn').forEach((button) => {
      gsap.set(button, { opacity: 0, y: 0, scale: 1 });
      gsap.to(button, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.3,
        ease: 'back.out(1.5)',
        scrollTrigger: once(button, 'top 90%'),
      });
    });
    // 11 · video players
    all(scope, '.video-player').forEach((player) => {
      gsap.set(player, { opacity: 0, scale: 0.92, willChange: 'transform, opacity' });
      gsap.to(player, {
        opacity: 1,
        scale: 1,
        duration: 1,
        ease: 'power3.out',
        clearProps: 'transform,filter,willChange',
        scrollTrigger: once(player, 'top 80%'),
      });
    });
  }, scope);

  return () => {
    detach.forEach((release) => {
      release();
    });
    context.revert();
  };
}

/**
 * Arms the reveal once for the mounted page. `key` identifies the page view (the pathname):
 * a new key reverts the previous page's effects and arms again. Under
 * `prefers-reduced-motion: reduce` (read at effect time) nothing is animated.
 */
export function useScrollReveal(scope: RefObject<HTMLElement | null>, key: string): void {
  useEffect(() => {
    let dispose: (() => void) | undefined;
    const timer = window.setTimeout(() => {
      const element = scope.current;
      if (element === null) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      dispose = armScrollReveal(element, window.innerWidth);
    }, REVEAL_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
      dispose?.();
    };
  }, [scope, key]);
}
